<script lang="ts">
  import { onMount } from "svelte";
  import AuthLayout from "./AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";
  import Dialog from "$lib/components/Dialog.svelte";
  import Field from "$lib/components/Field.svelte";
  import Select from "$lib/components/Select.svelte";
  import {
    approveMcpPairing,
    denyMcpPairing,
    listMcpServers,
    resolveMcpPairing,
    type McpPairingSummary,
    type McpServer
  } from "$lib/api/mcp";
  import { AppError, unexpectedError } from "$lib/api/problem";
  import { createKeyRotator, fingerprintOf } from "$lib/api/idempotency";
  import { can, hasFeature } from "$lib/auth/permissions";
  import { MCP_SETUP_COMMAND, mcpServerState, normalizePairingCode, replaceableServers } from "$lib/domain/mcp";
  import { formatDateTime } from "$lib/format/dates";
  import { prefs } from "$lib/stores/prefs.svelte";
  import { session } from "$lib/stores/session.svelte";
  import { router } from "../../router.svelte";

  // `/mcp/pair` — the approval half of the device pairing `epidev-mcp setup` starts.
  // Accepts `?code=` and `?pairingId=` (the CLI's verificationUrlComplete), `?org=` to
  // preselect an organization, and `?replace=` from the Servers view's Replace action.
  type Step = "enter" | "review" | "replace" | "approved" | "denied";

  const eligible = $derived(session.organizations.filter((org) => hasFeature(org, "portal.mcp_server")));
  let orgId = $state("");
  const organization = $derived(eligible.find((org) => org.id === orgId) ?? null);
  const canApprove = $derived(can(organization, "mcp.manage"));
  const orgOptions = $derived(eligible.map((org) => ({ value: org.id, label: org.name })));

  let code = $state("");
  let expectedPairingId = $state<string | null>(null);
  let step = $state<Step>("enter");
  let pairing = $state<McpPairingSummary | null>(null);
  let servers = $state<McpServer[]>([]);
  let replaceServerId = $state<string | null>(null);
  let requestedReplace = $state<string | null>(null);
  let approvedServer = $state<McpServer | null>(null);
  let busy = $state(false);
  let error = $state<AppError | null>(null);
  let codeError = $state<string | undefined>(undefined);
  let permissionDenied = $state(false);
  let confirmReplaceOpen = $state(false);
  const approveKeys = createKeyRotator();
  const denyKeys = createKeyRotator();

  const replaceTarget = $derived(servers.find((server) => server.id === replaceServerId) ?? null);
  const replaceChoices = $derived(replaceableServers(servers));

  onMount(() => {
    const query = router.match.query;
    const requestedOrg = query.get("org");
    const initial = eligible.find((org) => org.id === requestedOrg)
      ?? eligible.find((org) => org.id === prefs.lastOrgId && can(org, "mcp.manage"))
      ?? eligible.find((org) => can(org, "mcp.manage"))
      ?? eligible[0];
    orgId = initial?.id ?? "";
    code = normalizePairingCode(query.get("code")) ?? "";
    expectedPairingId = query.get("pairingId");
    requestedReplace = query.get("replace");
    // The code is a short-lived approval secret: keep it out of history and referrers.
    router.scrubQuery(["code", "pairingId"]);
    if (code && canApprove) void lookup();
  });

  function reset(): void {
    step = "enter";
    pairing = null;
    servers = [];
    replaceServerId = null;
    approvedServer = null;
    error = null;
    codeError = undefined;
    permissionDenied = false;
  }

  function changeOrg(value: string): void {
    orgId = value;
    reset();
  }

  function handle(caught: unknown): void {
    const failure = unexpectedError(caught);
    if (failure.status === 403 && failure.code === "permission_denied") {
      permissionDenied = true;
      return;
    }
    if (failure.code === "invalid_user_code" || failure.code === "pairing_not_found") {
      step = "enter";
      codeError = failure.userMessage;
      return;
    }
    error = failure;
  }

  async function lookup(): Promise<void> {
    const normalized = normalizePairingCode(code);
    codeError = undefined;
    error = null;
    if (!normalized) {
      codeError = "Enter the eight-letter code shown by setup, for example BCDF-GHJK.";
      return;
    }
    code = normalized;
    busy = true;
    try {
      const found = await resolveMcpPairing(orgId, normalized);
      if (expectedPairingId && found.pairingId !== expectedPairingId) {
        codeError = "This code belongs to a different setup request than the link you opened. Check the code in your terminal.";
        return;
      }
      pairing = found;
      step = "review";
      if (requestedReplace) {
        servers = await listMcpServers(orgId);
        replaceServerId = replaceableServers(servers).some((server) => server.id === requestedReplace) ? requestedReplace : null;
      }
    } catch (caught) {
      handle(caught);
    } finally {
      busy = false;
    }
  }

  async function approve(): Promise<void> {
    if (!pairing || busy) return;
    busy = true;
    error = null;
    try {
      approvedServer = await approveMcpPairing(
        orgId,
        pairing.pairingId,
        { userCode: code, replaceServerId },
        approveKeys.key(fingerprintOf(pairing.pairingId, replaceServerId ?? ""))
      );
      confirmReplaceOpen = false;
      step = "approved";
      prefs.setLastOrg(orgId);
    } catch (caught) {
      confirmReplaceOpen = false;
      const failure = unexpectedError(caught);
      if (failure.code === "server_limit_exceeded" && !replaceServerId) {
        try {
          servers = await listMcpServers(orgId);
        } catch (loadError) {
          handle(loadError);
          return;
        }
        replaceServerId = replaceableServers(servers)[0]?.id ?? null;
        step = "replace";
        return;
      }
      if (failure.code === "expired" || failure.code === "pairing_not_pending") {
        error = failure;
        step = "enter";
        pairing = null;
        return;
      }
      handle(failure);
    } finally {
      busy = false;
    }
  }

  async function deny(): Promise<void> {
    if (!pairing || busy) return;
    busy = true;
    error = null;
    try {
      await denyMcpPairing(orgId, pairing.pairingId, code, denyKeys.key(pairing.pairingId));
      step = "denied";
    } catch (caught) {
      handle(caught);
    } finally {
      busy = false;
    }
  }

  function onApproveClick(): void {
    if (replaceServerId) confirmReplaceOpen = true;
    else void approve();
  }

  function onSubmit(event: SubmitEvent): void {
    event.preventDefault();
    void lookup();
  }
</script>

<AuthLayout title="Approve an MCP server" subtitle={`Signed in as ${session.user?.email ?? ""}`}>
  <div class="stack">
    {#if eligible.length === 0}
      <Banner tone="info" title="No organization can link an MCP server" role="status">
        None of your organizations has an active EpiDev MCP subscription. An owner can add one from Billing.
      </Banner>
      <Button href="/" variant="ghost" fullWidth>Open customer portal</Button>
    {:else}
      {#if eligible.length > 1 && step !== "approved" && step !== "denied"}
        <Select id="mcp-pair-org" label="Organization" options={orgOptions} value={orgId} onchange={changeOrg} />
      {/if}

      {#if !canApprove || permissionDenied}
        <div class="denied" role="status">
          <h2 class="denied-title">Only an owner or admin can approve servers</h2>
          <p>
            Approving an MCP server for {organization?.name ?? "this organization"} requires the admin role. Ask an owner
            or admin to open this page and enter the code shown by setup.
          </p>
        </div>
        <Button href="/" variant="ghost" fullWidth>Open customer portal</Button>
      {:else}
        {#if error}
          <Banner tone="error" title={error.code === "expired" ? "This pairing request expired" : "We couldn't complete that"} correlationId={error.correlationId}>
            {error.code === "expired" ? `Run ${MCP_SETUP_COMMAND} again for a new code.` : error.userMessage}
          </Banner>
        {/if}

        {#if step === "enter"}
          <form class="stack" onsubmit={onSubmit} novalidate>
            <p class="copy">
              Enter the pairing code that <span class="mono">{MCP_SETUP_COMMAND}</span> printed in the terminal on the server host.
            </p>
            <Field
              id="mcp-pair-code"
              label="Pairing code"
              bind:value={code}
              placeholder="BCDF-GHJK"
              autocomplete="off"
              mono
              error={codeError}
            />
            <Button type="submit" fullWidth loading={busy}>Continue</Button>
          </form>
        {:else if step === "review" && pairing}
          <section class="review" aria-labelledby="mcp-pair-review">
            <h2 id="mcp-pair-review" class="review-title">{pairing.serverName}</h2>
            <dl class="facts">
              <div><dt>Fingerprint</dt><dd class="mono">{pairing.fingerprint}</dd></div>
              <div><dt>Version</dt><dd>{pairing.serverVersion ?? "—"}</dd></div>
              {#if pairing.hostFingerprint}<div><dt>Host</dt><dd class="mono">{pairing.hostFingerprint}</dd></div>{/if}
              <div><dt>Code</dt><dd class="mono">{code}</dd></div>
              <div><dt>Expires</dt><dd>{formatDateTime(pairing.expiresAt)}</dd></div>
            </dl>
            <p class="copy">
              Approve only if this fingerprint matches the one setup printed. Approval links the server to
              {organization?.name} and uses one server license.
            </p>
            {#if replaceTarget}
              <Banner tone="warn" title={`Replaces ${replaceTarget.name}`} role="status">
                {replaceTarget.name} is unlinked when you approve. Its permissions and environment settings move to this server; ERP credentials must be entered again.
              </Banner>
            {/if}
          </section>
          <Button fullWidth loading={busy} onclick={onApproveClick}>{replaceTarget ? "Approve and replace" : "Approve server"}</Button>
          <Button variant="ghost" fullWidth disabled={busy} onclick={deny}>Deny</Button>
        {:else if step === "replace" && pairing}
          <section class="stack" aria-labelledby="mcp-pair-replace">
            <h2 id="mcp-pair-replace" class="review-title">Every server license is in use</h2>
            <p class="copy">
              {organization?.name} has no free MCP server license. If {pairing.serverName} replaces a server (for example a
              rebuilt host), choose it below. The replaced server is unlinked, and its permissions and environment settings
              move to {pairing.serverName}. ERP credentials do not carry over.
            </p>
            {#if replaceChoices.length === 0}
              <Banner tone="info" title="Nothing to replace" role="status">Add a server license from Billing, then approve again.</Banner>
            {:else}
              <fieldset class="choices">
                <legend class="sr-only">Server to replace</legend>
                {#each replaceChoices as server (server.id)}
                  {@const serverState = mcpServerState(server)}
                  <label class="choice">
                    <input type="radio" name="mcp-replace" value={server.id} bind:group={replaceServerId} />
                    <span class="choice-text">
                      <span class="choice-name">Replace {server.name}</span>
                      <span class="choice-meta">{serverState.label} · <span class="mono">{server.fingerprint}</span></span>
                    </span>
                  </label>
                {/each}
              </fieldset>
              <Button fullWidth disabled={!replaceServerId} loading={busy} onclick={() => (confirmReplaceOpen = true)}>Replace server</Button>
            {/if}
            <Button variant="ghost" fullWidth disabled={busy} onclick={deny}>Deny</Button>
          </section>
        {:else if step === "approved" && approvedServer}
          <Banner tone="ok" title="Server approved" role="status">
            Return to your terminal: setup collects its license and finishes on its own.
          </Banner>
          <dl class="facts">
            <div><dt>Server</dt><dd>{approvedServer.name}</dd></div>
            <div><dt>Server ID</dt><dd class="mono">{approvedServer.id}</dd></div>
            <div><dt>Fingerprint</dt><dd class="mono">{approvedServer.fingerprint}</dd></div>
          </dl>
          <Button href={`/orgs/${orgId}/mcp`} fullWidth>Open MCP server</Button>
        {:else if step === "denied"}
          <Banner tone="info" title="Request denied" role="status">
            Setup on that host stops. Nothing was linked and no license was used.
          </Banner>
          <Button href={`/orgs/${orgId}/mcp`} variant="ghost" fullWidth>Open MCP server</Button>
        {/if}
      {/if}
    {/if}
  </div>
</AuthLayout>

<Dialog open={confirmReplaceOpen} title={`Replace ${replaceTarget?.name ?? "server"}?`} dismissable={!busy} onclose={() => (confirmReplaceOpen = false)}>
  <p class="copy">
    {replaceTarget?.name ?? "The selected server"} is unlinked immediately: its license session, portal link, and portal-issued
    tokens are revoked. {pairing?.serverName ?? "The new server"} inherits its permissions and environment settings. You will
    need to enter ERP credentials again on the new server.
  </p>
  {#snippet footer()}
    <Button variant="ghost" disabled={busy} onclick={() => (confirmReplaceOpen = false)}>Cancel</Button>
    <Button variant="danger" loading={busy} onclick={approve}>Replace and approve</Button>
  {/snippet}
</Dialog>

<style>
  .copy {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
  }
  .review {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .review-title,
  .denied-title {
    margin: 0;
    color: var(--heading);
    font-size: 16px;
    font-weight: 650;
  }
  .denied {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .denied p {
    margin: 0;
    color: var(--text-soft);
    font-size: 14px;
    line-height: 1.55;
  }
  .facts {
    display: grid;
    gap: 8px;
    margin: 0;
  }
  .facts div {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 10px;
  }
  .facts dt {
    color: var(--muted);
    font-size: 13px;
  }
  .facts dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
    font-size: 13.5px;
  }
  .choices {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    border: none;
  }
  .choice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid var(--line-soft);
    border-radius: var(--radius-btn);
    background: var(--surface);
    cursor: pointer;
  }
  .choice:has(input:checked) {
    border-color: var(--accent-border);
  }
  .choice input {
    margin-top: 3px;
    accent-color: var(--accent);
  }
  .choice-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .choice-name {
    color: var(--heading);
    font-weight: 600;
    font-size: 14px;
  }
  .choice-meta {
    color: var(--muted);
    font-size: 12.5px;
    overflow-wrap: anywhere;
  }
</style>
